import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState, isCartEmpty } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { fontFamilyAlt, mediaQueriesBP } from "@waytek/Utilities/variables";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CartHeaderStyles {
    container?: GridContainerProps;
    gridItem1?: GridItemProps;
    gridItem2?: GridItemProps;
    approverInfoGridItem?: GridItemProps;
    vmiInfoGridItem?: GridItemProps;
    vmiMessageWrapper?: InjectableCss;
    vmiMessageText?: TypographyPresentationProps;
    warningIcon?: IconPresentationProps;

    globalCount?: InjectableCss;
}

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    return {
        isCartEmpty: isCartEmpty(cart),
        isAuthenticated: state.context?.session?.isAuthenticated && !state.context?.session?.isGuest,
        /* Verndale Custom Feature */
        cart,
    };
};

export const cartHeaderStyles: CartHeaderStyles = {
    /* Verndale Custom Feature - START */
    gridItem1: {
        width: [11, 11, 11, 8, 8],
        css: css`
            ${mediaQueriesBP.maxLG} {
                flex-basis: calc(100% - 40px);
                max-width: calc(100% - 40px);
            }

            ${mediaQueriesBP.minLG} {
                padding-right: 0;
            }

            > div {
                ${mediaQueriesBP.minLG} {
                    border-bottom: 2px solid ${getColor("common.global.industrialTaupe")};
                }

                h1 {
                    margin-bottom: 0;
                }
            }
        `,
    },
    gridItem2: {
        width: [1, 1, 1, 4, 4],
        css: css`
            ${mediaQueriesBP.maxLG} {
                flex-basis: 40px;
                max-width: 40px;
            }
        `,
    },
    approverInfoGridItem: {
        width: 12,
        css: css`
            padding: 0 15px;
        `,
    },
    /* Verndale Custom Feature - END */
    vmiInfoGridItem: {
        width: 12,
        css: css`
            padding: 0 15px;
        `,
    },
    vmiMessageWrapper: {
        css: css`
            border: 2px solid ${getColor("danger.main")};
            padding: 15px 10px;
            display: flex;
            width: 100%;
        `,
    },
    vmiMessageText: {
        css: css`
            width: 100%;
        `,
    },
    warningIcon: {
        src: "AlertCircle",
        color: "danger.main",
        css: css`
            margin-right: 10px;
        `,
    },

    /* Verndale Custom Feature */
    globalCount: {
        css: css`
            color: ${getColor("common.global.secondaryGray")};
            font-family: ${fontFamilyAlt};
            font-size: 16px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.5;
            letter-spacing: normal;
            flex-grow: 1;
            width: 100%;
            height: 100%;
            justify-content: flex-end;
            display: flex;
            align-items: flex-end;
            padding-bottom: 5px;

            ${mediaQueriesBP.minLG} {
                padding-right: 0;
            }
        `,
    },
};

const styles = cartHeaderStyles;

const CartHeader: FC<Props> = ({ id, isCartEmpty, cart, isAuthenticated }) => {
    const currentMode = getCookie(NavigationModeCookieName);
    const shouldShowVmiMessage = !isCartEmpty && isAuthenticated && currentMode === "Vmi";
    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.gridItem1}>
                <Zone contentId={id} zoneName="Content0" />
                {/* Verndale Custom Feature - START */}
                <StyledWrapper {...styles.globalCount}>
                    {cart?.totalCountDisplay} {translate("Products")}
                </StyledWrapper>
                {/* Verndale Custom Feature - END */}
            </GridItem>
            <GridItem {...styles.gridItem2}>
                <Zone contentId={id} zoneName="Content1" />
            </GridItem>
            <GridItem {...styles.approverInfoGridItem}>
                <Zone contentId={id} zoneName="Content2" />
            </GridItem>
            <GridItem {...styles.vmiInfoGridItem}>
                {shouldShowVmiMessage && (
                    <StyledWrapper {...styles.vmiMessageWrapper}>
                        <Icon {...styles.warningIcon}></Icon>
                        <Typography {...styles.vmiMessageText}>{siteMessage("Cart_View_On_Vmi")}</Typography>
                    </StyledWrapper>
                )}
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CartHeader),
    definition: {
        group: "Cart",
        displayName: "Page Header",
        allowedContexts: ["CartPage"],
    },
};

export default widgetModule;
